import React, { useRef, useEffect } from 'react';
import Immutable, { OrderedMap } from 'immutable';
import { Container, WysiwygField, Image } from '@uncinc/uncinc-react-kitchen-sink';
import { CoreLink, Paragraph } from '@uncinc/react-drupal-core';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import {
  SCREEN_MOBILE,
  SCREEN_NORMAL,
  SCREEN_TABLET,
  OVERVIEW_REDIRECT,
  CONTENT_IMAGE_PLACEHOLDER,
  DEFAULT_LANGUAGE,
} from '../../../config/constants';


import Marker from '../../View/LocationMarkers/Marker';
import LocationsMap from '../../../components/LocationsMap';

import './style.scss';

const TaxonomyLocation = ({ entity }) => {
  const markerRef = useRef([]);
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => state.getIn(['ui', 'language']) || DEFAULT_LANGUAGE);

  const title = entity.getIn(['name', 0, 'value']);
  const description = entity.getIn(['description', 0, 'processed']);

  const email = entity.getIn(['field_email', 0, 'value']);
  const openhours = entity.getIn(['field_openhours', 0, 'value']);
  const phone = entity.getIn(['field_phone', 0, 'value']);
  const website = entity.getIn(['field_website', 0, 'value']);
  const hero = entity.getIn(['field_image', 0, 'styles'], null);
  const addressLine1 = entity.getIn(['field_address', 0, 'address_line1']);
  const addressLine2 = entity.getIn(['field_address', 0, 'address_line2']);
  const city = entity.getIn(['field_address', 0, 'locality']);
  const postalCode = entity.getIn(['field_address', 0, 'postal_code']);
  const entityOverviewUrl = OVERVIEW_REDIRECT['locations'][currentLanguage];
  // setup schema.org jobPosting
  // get protocol and host from window location
  const pathname = useSelector((state) => state.getIn(['router', 'location', 'pathname']));
  const protocol = window.location.protocol;
  const host = window.location.host;
  const url = `${protocol}//${host}${pathname}`;
  const jobPostingLinkedData = createPlaceLd(entity)
    .set('url', url);
  const schemaString = JSON.stringify(jobPostingLinkedData.toJS(), null, 2);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <>
      <Helmet>
        <html lang={currentLanguage} />
        <script type="application/ld+json">
          {schemaString}
        </script>
      </Helmet>

      <div className="Location TaxonomyLocation">

        <Container>
          <div className="row">
            <div className="col-12 col-lg-7 offset-lg-5 NodeVacancyFull__heroimage">
              {hero ? (
                <Image
                  lazyload
                  includeWebP
                  alt={title}
                  src={{
                    [SCREEN_MOBILE]: hero.getIn(['crop_1_1']),
                    [SCREEN_TABLET]: hero.getIn(['crop_3_2']),
                    [SCREEN_NORMAL]: hero.getIn(['crop_4_3']),
                  }}
                />
              ) : <img src={CONTENT_IMAGE_PLACEHOLDER} alt="Content placeholder" />}
            </div>
            <div className="col-12 col-md-10 col-lg-6 NodeVacancyFull__titleboxWrapper">
              <div className="NodeVacancyFull__titlebox">
                <div>
                  <CoreLink className="link" to={entityOverviewUrl}>
                    <i className="icon icon-arrow-left" /> {t('locations.back_to_overview')}
                  </CoreLink>
                </div>
                <div>
                  <h1 className="asH2">{title}</h1>
                </div>
              </div>

              <div className="Location__details">
                <div>
                  <i className="icon icon-pin2" />
                  <div>
                    <p>{addressLine1}</p>
                    {addressLine2 && <p>{addressLine2}</p>}
                    <p>
                      {postalCode} {city}
                    </p>
                  </div>
                </div>

                {openhours && (
                  <div>
                    <i className="icon icon-time" />
                    <div>
                      {openhours}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="Node__body">

              <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
                <WysiwygField
                  property="schema:text"
                  html={description}
                />
              </div>
            </div>
          </div>
        </Container>


        {entity.get('field_paragraphs', Immutable.List()).size > 0 ? (
          <div className="TaxonomyMunicipalities__paragraphs">
            {entity.get('field_paragraphs', Immutable.List()).map((paragraph) => {
              const id = paragraph.get('target_id');
              return (
                <Paragraph
                  key={`paragraph:${id}`}
                  id={id}
                  entity={paragraph.get('entity')}
                />
              );
            })}
          </div>
        ) : null}

        <Container>

          {(phone || email || website) && (
            <div className="row Location__contact">
              <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
                <h3>{t('general.contact')}</h3>
                {phone && (
                  <p>
                    <i className="icon icon-phone" />
                    {phone}
                  </p>
                )}
                {email && (
                  <p>
                    <i className="icon icon-mail" />
                    <a href={`mailto: ${email}`}>{email}</a>
                  </p>
                )}
                {website && (
                  <p>
                    <i className="icon icon-web" />
                    <a href={website}>{website}</a>
                  </p>
                )}
              </div>
            </div>
          )}

        </Container>

        <LocationsMap>
          <Marker reference={markerRef} entity={entity} taxonomyPage />
        </LocationsMap>
      </div>
    </>
  );
};

export default TaxonomyLocation;

function createPlaceLd(entity) {
  const title = entity.getIn(['name', 0, 'value']);
  const description = entity.getIn(['description', 0, 'value'], '')?.replace(/(<([^>]+)>)/ig, '');
  const image = entity.getIn(['field_image', 0, 'styles', 'thumbnail'], null);
  const addressLine1 = entity.getIn(['field_address', 0, 'address_line1']);
  const addressLine2 = entity.getIn(['field_address', 0, 'address_line2']);
  const city = entity.getIn(['field_address', 0, 'locality']);
  const postalCode = entity.getIn(['field_address', 0, 'postal_code']);

  let addressString = addressLine1;
  if (addressLine2) {
    addressString += ' ' + addressLine2;
  }

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Place',
    'name': title,
    'description': description,
    'image': image,
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': addressString,
      'addressLocality': city,
      'postalCode': postalCode,
      'addressCountry': 'NL',
    },
  };

  return OrderedMap(schema);
}
